<template>
    <div class="notifications-page">
      <header class="notifications-header">
        <h1 class="page-title">Notifications</h1>
        <button
          v-if="unreadCount > 0"
          class="mark-all-read"
          @click="markAllAsRead"
        >
          <i class="fas fa-check-double"></i>
          Mark all read
        </button>
      </header>

      <div v-if="loading" class="loading">
        <LoadingSpinner />
      </div>

      <div v-else-if="filteredNotifications.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-bell-slash"></i>
        </div>
        <p class="empty-title">You're all caught up</p>
        <p class="empty-sub">New likes, tips and follows will show up here.</p>
      </div>

      <div v-else class="notifications-list">
        <!-- Email Confirmation Alert -->
        <div v-if="shouldShowEmailConfirmation" class="email-confirmation-alert">
          <div class="alert-content">
            <i class="fas fa-envelope"></i>
            <p>Please confirm your email address to fully activate your account. The confirmation email may be in your spam folder.</p>
            <button 
              v-if="!emailSentSuccessfully" 
              @click="resendConfirmationEmail" 
              class="resend-email-btn" 
              :disabled="resendingEmail">
              {{ buttonText }}
            </button>
            <span v-else class="email-sent-message">Confirmation email sent!</span>
          </div>
        </div>

        <template v-for="group in groupedNotifications" :key="group.label">
          <section v-if="group.items.length" class="notification-group">
            <h2 class="group-label">{{ group.label }}</h2>

            <div
              v-for="notification in group.items"
              :key="notification.id"
              class="notification-item"
              :class="{ unread: !notification.is_read }"
              role="button"
              tabindex="0"
              @click="handleNotificationClick(notification)"
              @keydown.enter="handleNotificationClick(notification)"
            >
              <div class="avatar-wrap">
                <img
                  v-if="notification.content_object && notification.content_object.user"
                  :src="notification.content_object.user.profile_pic || defaultAvatar"
                  :alt="notification.content_object.user.username"
                  class="profile-pic"
                />
                <div v-else class="profile-pic placeholder-avatar" :class="notificationType(notification)">
                  <i :class="typeIcon(notification)"></i>
                </div>
                <span
                  v-if="notification.content_object && notification.content_object.user"
                  class="type-badge"
                  :class="notificationType(notification)"
                >
                  <i :class="typeIcon(notification)"></i>
                </span>
              </div>

              <div class="notification-content">
                <p class="notification-message">{{ notification.message }}</p>
                <p
                  class="notification-preview"
                  v-if="notification.content_object && notification.content_object.text"
                >
                  {{ truncateText(notification.content_object.text, 75) }}
                </p>
                <span class="notification-time">{{ formatTime(notification.created_at) }}</span>
              </div>

              <span class="unread-dot" v-if="!notification.is_read"></span>
            </div>
          </section>
        </template>

        <div v-if="hasMoreNotifications" class="load-more">
          <button @click="loadMoreNotifications" :disabled="loadingMore">
            {{ loadingMore ? 'Loading...' : 'Load More' }}
          </button>
        </div>
        
        <!-- Profile Update Suggestion -->
        <div v-if="shouldShowProfileUpdate" class="profile-update-suggestion">
          <div class="suggestion-content">
            <i class="fas fa-user-edit"></i>
            <p>Complete your profile to connect with more skiers</p>
            <button @click="goToProfileEdit" class="update-profile-btn">
              Update your Profile
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
import { API_BASE_URL } from '@/config';
  import axios from 'axios';
  import LoadingSpinner from '@/components/LoadingSpinner.vue';
  import eventBus from '@/utils/eventBus';
  
  export default {
    name: 'NotificationsPage',
    components: {
      LoadingSpinner
    },
    data() {
      return {
        notifications: [],
        loading: true,
        loadingMore: false,
        offset: 0,
        limit: 20,
        hasMoreNotifications: false,
        userProfile: null,
        profileLoading: true,
        resendingEmail: false,
        emailSentSuccessfully: false,
        defaultAvatar: 'https://6buoys-bucket.s3.amazonaws.com/avatar.png'
      };
    },
    created() {
      this.fetchNotifications();
      this.fetchUserProfile();
    },
    methods: {
      async fetchNotifications() {
        const token = localStorage.getItem('access_token');
        if (!token) {
          this.$router.push('/login');
          return;
        }
        
        try {
          this.loading = true;
          const response = await axios.get(`${API_BASE_URL}/api/notifications/`, {
            headers: {
              Authorization: `Bearer ${token}`
            },
            params: {
              limit: this.limit,
              offset: this.offset
            }
          });

          
          this.notifications = response.data;
          this.hasMoreNotifications = response.data.length === this.limit;
          this.loading = false;
        } catch (error) {
          console.error('Error fetching notifications:', error);
          this.loading = false;
        }
      },
      
      async loadMoreNotifications() {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        try {
          this.loadingMore = true;
          this.offset += this.limit;
          
          const response = await axios.get(`${API_BASE_URL}/api/notifications/`, {
            headers: {
              Authorization: `Bearer ${token}`
            },
            params: {
              limit: this.limit,
              offset: this.offset
            }
          });
          
          this.notifications = [...this.notifications, ...response.data];
          this.hasMoreNotifications = response.data.length === this.limit;
          this.loadingMore = false;
        } catch (error) {
          console.error('Error loading more notifications:', error);
          this.loadingMore = false;
        }
      },
      
      async handleNotificationClick(notification) {
        // Mark as read if it's unread
        if (!notification.is_read) {
          await this.markAsRead(notification.id);
          
          // Use the eventBus instead of $root.$emit
          eventBus.emit('notification-read');
        }
        
        // Navigate to the linked content if available
        if (notification.link) {
          this.$router.push(notification.link);
        }
      },
      
      async markAsRead(notificationId) {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        try {
          // Updated endpoint to match the backend URL structure
          await axios.post(`${API_BASE_URL}/api/notifications/mark-read/${notificationId}/`, {}, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          // Update the local state - Vue 3 way
          const index = this.notifications.findIndex(n => n.id === notificationId);
          if (index !== -1) {
            // Create a new array with the updated notification
            this.notifications = [
              ...this.notifications.slice(0, index),
              { ...this.notifications[index], is_read: true },
              ...this.notifications.slice(index + 1)
            ];
          }
        } catch (error) {
          console.error('Error marking notification as read:', error);
        }
      },
      
      async markAllAsRead() {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        try {
          await axios.post(`${API_BASE_URL}/api/notifications/mark-all-read/`, {}, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          // Update local state to mark all notifications as read
          this.notifications = this.notifications.map(notification => ({
            ...notification,
            is_read: true
          }));
          
          // Emit event to update notification counts elsewhere in the app
          eventBus.emit('notification-read');
        } catch (error) {
          console.error('Error marking all notifications as read:', error);
        }
      },
      
      formatTime(timestamp) {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);
        
        if (diffInSeconds < 60) {
          return 'just now';
        } else if (diffInSeconds < 3600) {
          const minutes = Math.floor(diffInSeconds / 60);
          return `${minutes}m ago`;
        } else if (diffInSeconds < 86400) {
          const hours = Math.floor(diffInSeconds / 3600);
          return `${hours}h ago`;
        } else if (diffInSeconds < 604800) {
          const days = Math.floor(diffInSeconds / 86400);
          return `${days}d ago`;
        } else {
          return date.toLocaleDateString();
        }
      },
      
      truncateText(text, maxLength) {
        // Increase maxLength by 20%
        const adjustedMaxLength = Math.floor(maxLength);
        if (text.length <= adjustedMaxLength) {
          return text;
        }
        return text.substring(0, adjustedMaxLength) + '...';
      },
      
      hasCurrencySymbol(text) {
        if (!text) return false;
        return text.includes('$') || text.includes('€');
      },

      notificationType(notification) {
        const msg = (notification.message || '').toLowerCase();
        if (this.hasCurrencySymbol(notification.message)) return 'payment';
        if (msg.includes('like')) return 'like';
        if (msg.includes('follow')) return 'follow';
        if (msg.includes('tip') || msg.includes('comment') || msg.includes('coaching') || msg.includes('reply')) return 'comment';
        return 'system';
      },

      typeIcon(notification) {
        const icons = {
          like: 'fas fa-heart',
          comment: 'fas fa-comment',
          follow: 'fas fa-user-plus',
          payment: 'fas fa-dollar-sign',
          system: 'fas fa-bell'
        };
        return icons[this.notificationType(notification)];
      },
      
      async fetchUserProfile() {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        try {
          const response = await axios.get(`${API_BASE_URL}/api/profile/`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          this.userProfile = response.data;
          this.profileLoading = false;
        } catch (error) {
          console.error('Error fetching user profile:', error);
          this.profileLoading = false;
        }
      },
      
      goToProfileEdit() {
        this.$router.push('/edit-profile');
      },
      
      async resendConfirmationEmail() {
        const token = localStorage.getItem('access_token');
        if (!token || !this.userProfile || !this.userProfile.email) return;
        
        try {
          this.resendingEmail = true;
          
          await axios.post(`${API_BASE_URL}/api/resend-confirmation-email/`, 
            { email: this.userProfile.email },
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          
          // Set a flag to indicate email was sent successfully
          this.emailSentSuccessfully = true;
          
        } catch (e) {
          console.error('Error sending confirmation email:', e);
          alert('Failed to send confirmation email. Please try again later.');
        } finally {
          this.resendingEmail = false;
        }
      }
    },
    computed: {
      filteredNotifications() {
        // Always return all notifications regardless of filter
        return this.notifications;
      },

      unreadCount() {
        return this.notifications.filter(n => !n.is_read).length;
      },

      groupedNotifications() {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekAgo = new Date(startOfToday);
        weekAgo.setDate(weekAgo.getDate() - 7);

        const groups = { today: [], week: [], earlier: [] };

        this.notifications.forEach(notification => {
          const created = new Date(notification.created_at);
          if (created >= startOfToday) {
            groups.today.push(notification);
          } else if (created >= weekAgo) {
            groups.week.push(notification);
          } else {
            groups.earlier.push(notification);
          }
        });

        return [
          { label: 'Today', items: groups.today },
          { label: 'This week', items: groups.week },
          { label: 'Earlier', items: groups.earlier }
        ];
      },
      
      shouldShowProfileUpdate() {
        if (this.profileLoading || !this.userProfile) return false;
        
        // Don't show profile update suggestion if email is not confirmed
        if (this.userProfile.confirmed_via_email === false) {
          return false;
        }
        
        // Check if bio is empty or the default "no bio..."
        const hasRealBio = this.userProfile.bio && 
                          this.userProfile.bio.trim().length > 0 && 
                          this.userProfile.bio !== "no bio...";
        
        // Check if using default avatar (contains "avatar.png" in the URL)
        const hasCustomAvatar = this.userProfile.avatar && 
                               !this.userProfile.avatar.includes('avatar.png');
        
        // Check if ski, lake, and PB are filled in
        const hasSkiInfo = this.userProfile.ski && this.userProfile.ski.trim().length > 0;
        const hasLakeInfo = this.userProfile.lake && this.userProfile.lake.trim().length > 0;
        const hasPBInfo = this.userProfile.PB && this.userProfile.PB.trim().length > 0;

        // Show update suggestion if any of these fields are missing
        return !hasRealBio || !hasCustomAvatar || !hasSkiInfo || !hasLakeInfo || !hasPBInfo;
      },
      
      shouldShowEmailConfirmation() {
        return this.userProfile && this.userProfile.confirmed_via_email === false;
      },
      
      buttonText() {
        if (this.resendingEmail) {
          return 'Sending...';
        } else if (this.emailSentSuccessfully) {
          return 'Confirmation email sent!';
        } else {
          return 'Resend Confirmation Email';
        }
      }
    }
  };
  </script>
  
  <style scoped>
  .notifications-page {
    max-width: 600px;
    margin: 0 auto;
    padding: 20px;
  }

  @media screen and (max-width: 600px) {
    .notifications-page {
      padding: 16px 12px 80px 12px;
    }
  }

  .notifications-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .page-title {
    margin: 0;
    font-size: 1.5rem;
    color: #ffffff;
  }

  .mark-all-read {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: none;
    color: #1da1f2;
    font-size: 0.85rem;
    font-weight: bold;
    cursor: pointer;
    padding: 6px 10px;
    border-radius: 20px;
    transition: background-color 0.2s ease;
  }

  .mark-all-read:hover {
    background-color: rgba(29, 161, 242, 0.1);
  }

  .loading, .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: #8899a6;
    text-align: center;
  }

  .empty-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-color: #192734;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
  }

  .empty-icon i {
    font-size: 1.6rem;
    color: #8899a6;
  }

  .empty-title {
    margin: 0 0 4px;
    color: #ffffff;
    font-size: 1.05rem;
    font-weight: bold;
  }

  .empty-sub {
    margin: 0;
    font-size: 0.9rem;
  }

  .notification-group {
    margin-bottom: 18px;
  }

  .group-label {
    margin: 0 0 8px 4px;
    font-size: 0.72rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #8899a6;
    font-weight: bold;
  }

  .notification-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 14px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    position: relative;
  }

  .notification-item + .notification-item {
    margin-top: 2px;
  }

  .notification-item:hover {
    background-color: #22303c;
  }

  .notification-item.unread {
    background-color: rgba(29, 161, 242, 0.1);
  }

  .notification-item.unread:hover {
    background-color: rgba(29, 161, 242, 0.16);
  }

  .avatar-wrap {
    position: relative;
    width: 44px;
    height: 44px;
    flex-shrink: 0;
  }

  .profile-pic {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 2px solid #1da1f2;
    object-fit: cover;
  }

  .placeholder-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #192734;
    border-color: #38444d;
    color: #8899a6;
  }

  .placeholder-avatar.payment {
    color: #f7b928;
  }

  .placeholder-avatar i {
    font-size: 1.1rem;
  }

  .type-badge {
    position: absolute;
    bottom: -2px;
    right: -2px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid #15202b;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .type-badge i {
    font-size: 0.6rem;
    color: #ffffff;
  }

  .type-badge.like { background-color: #e0245e; }
  .type-badge.comment { background-color: #1da1f2; }
  .type-badge.follow { background-color: #17bf63; }
  .type-badge.payment { background-color: #f7b928; }
  .type-badge.system { background-color: #8899a6; }

  .notification-content {
    flex: 1;
    min-width: 0;
    text-align: left;
  }

  .notification-message {
    margin: 0 0 3px 0;
    color: #ffffff;
    font-size: 0.95rem;
    line-height: 1.35;
  }

  .notification-preview {
    margin: 0 0 3px 0;
    color: #8899a6;
    font-size: 0.85rem;
    font-style: italic;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .notification-time {
    font-size: 0.78rem;
    color: #8899a6;
  }

  .unread-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #1da1f2;
    flex-shrink: 0;
  }
  
  .load-more {
    display: flex;
    justify-content: center;
    padding: 15px;
  }
  
  .load-more button {
    background-color: transparent;
    border: 1px solid #1da1f2;
    color: #1da1f2;
    padding: 8px 16px;
    border-radius: 20px;
    cursor: pointer;
    font-weight: bold;
    transition: background-color 0.2s ease;
  }
  
  .load-more button:hover:not(:disabled) {
    background-color: rgba(29, 161, 242, 0.1);
  }
  
  .load-more button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  /* Profile Update Suggestion Styles */
  .profile-update-suggestion {
    margin-top: 20px;
    padding: 20px;
    border-radius: 10px;
    background-color: rgba(29, 161, 242, 0.1);
    border: 1px solid #38444d;
  }
  
  .suggestion-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  .suggestion-content i {
    font-size: 2rem;
    color: #1da1f2;
    margin-bottom: 15px;
  }
  
  .suggestion-content p {
    margin-bottom: 15px;
    color: #ffffff;
  }
  
  .update-profile-btn {
    background-color: #1da1f2;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 20px;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }
  
  .update-profile-btn:hover {
    background-color: #0c8ddb;
  }
  
  /* Email Confirmation Alert Styles */
  .email-confirmation-alert {
    margin-bottom: 20px;
    padding: 20px;
    border-radius: 10px;
    background-color: rgba(255, 193, 7, 0.1);
    border: 1px solid #ffc107;
  }
  
  .alert-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  .alert-content i {
    font-size: 2rem;
    color: #ffc107;
    margin-bottom: 15px;
  }
  
  .alert-content p {
    margin-bottom: 15px;
    color: #ffffff;
  }
  
  .resend-email-btn {
    background-color: #ffc107;
    color: #333;
    border: none;
    padding: 10px 20px;
    border-radius: 20px;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }
  
  .resend-email-btn:hover:not(:disabled) {
    background-color: #e0a800;
  }
  
  .resend-email-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  .email-sent-message {
    margin-left: 10px;
    color: #1da1f2;
    font-weight: bold;
  }
  </style>